import { ArticleService } from '@/types'
import { Checkbox, Collapse, Group, NumberInput, Paper, Table, Text, UnstyledButton } from '@mantine/core'
import { IconChevronRight } from '@tabler/icons-react'
import { ChangeEvent, memo, MouseEvent } from 'react'

export default function CatalogArticleGroup({
  articleName,
  services,
  curatedCatalog,
  overrides,
  isOpen,
  onToggleOpen,
  onSetEnabled,
  onSetPrice,
  onToggleAll,
}: {
  articleName: string
  services: ArticleService[]
  curatedCatalog: boolean
  overrides: Record<number, { enabled: boolean; price: number | null }>
  isOpen: boolean
  onToggleOpen: (articleName: string) => void
  onSetEnabled: (articleServiceId: number, value: boolean) => void
  onSetPrice: (articleServiceId: number, value: number | null) => void
  onToggleAll: (services: ArticleService[], value: boolean) => void
}) {
  const isEnabled = (id: number) => curatedCatalog ? (overrides[id]?.enabled ?? false) : true
  const priceFor = (id: number) => overrides[id]?.price ?? null

  const allEnabled = services.every((service) => isEnabled(service.id))
  const someEnabled = services.some((service) => isEnabled(service.id))
  const enabledCount = services.filter((service) => isEnabled(service.id)).length
  const customPriceCount = services.filter((service) => priceFor(service.id) !== null).length

  const handleHeaderClick = () => onToggleOpen(articleName)
  const handleToggleAll = (event: ChangeEvent<HTMLInputElement>) => onToggleAll(services, event.currentTarget.checked)
  const stopPropagation = (event: MouseEvent) => event.stopPropagation()

  return (
    <Paper withBorder radius="md" data-testid={`article-group-${articleName}`}>
      <Group justify="space-between" p="md" wrap="nowrap">
        <UnstyledButton onClick={handleHeaderClick} style={{ flex: 1 }}>
          <Group gap="sm" wrap="nowrap">
            <IconChevronRight
              size={16}
              style={{
                transform: isOpen ? 'rotate(90deg)' : undefined,
                transition: 'transform 150ms ease',
              }}
            />
            <Text fw={700} size="lg">
              {articleName}
            </Text>
            <Text c="dimmed" size="sm">
              {curatedCatalog
                ? `${enabledCount}/${services.length} activé${enabledCount > 1 ? 's' : ''}`
                : `${services.length} service${services.length > 1 ? 's' : ''}`}
              {customPriceCount > 0 &&
                ` · ${customPriceCount} prix personnalisé${customPriceCount > 1 ? 's' : ''}`}
            </Text>
          </Group>
        </UnstyledButton>
        {curatedCatalog && (
          <Checkbox
            checked={allEnabled}
            indeterminate={!allEnabled && someEnabled}
            onChange={handleToggleAll}
            onClick={stopPropagation}
            label="Tout activer"
          />
        )}
      </Group>

      <Collapse in={isOpen}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              {curatedCatalog && <Table.Th style={{ width: 80 }}>Activé</Table.Th>}
              <Table.Th>Service</Table.Th>
              <Table.Th style={{ width: 160 }}>Prix par défaut</Table.Th>
              <Table.Th style={{ width: 200 }}>Prix personnalisé</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {services.map((service) => (
              <CatalogArticleServiceRow
                key={service.id}
                service={service}
                curatedCatalog={curatedCatalog}
                enabled={isEnabled(service.id)}
                price={priceFor(service.id)}
                onSetEnabled={onSetEnabled}
                onSetPrice={onSetPrice}
              />
            ))}
          </Table.Tbody>
        </Table>
      </Collapse>
    </Paper>
  )
}

const CatalogArticleServiceRow = memo(function CatalogArticleServiceRow({
  service,
  curatedCatalog,
  enabled,
  price,
  onSetEnabled,
  onSetPrice,
}: {
  service: ArticleService
  curatedCatalog: boolean
  enabled: boolean
  price: number | null
  onSetEnabled: (articleServiceId: number, value: boolean) => void
  onSetPrice: (articleServiceId: number, value: number | null) => void
}) {
  const handleEnabledChange = (event: ChangeEvent<HTMLInputElement>) =>
    onSetEnabled(service.id, event.currentTarget.checked)
  const handlePriceChange = (value: string | number) =>
    onSetPrice(service.id, value === '' ? null : Number(value))

  return (
    <Table.Tr>
      {curatedCatalog && (
        <Table.Td>
          <Checkbox checked={enabled} onChange={handleEnabledChange} />
        </Table.Td>
      )}
      <Table.Td>
        <Text c={enabled ? undefined : 'dimmed'}>{service.service_name}</Text>
      </Table.Td>
      <Table.Td>
        <Text c="dimmed">{Number(service.contract_price).toFixed(2)}€</Text>
      </Table.Td>
      <Table.Td>
        <NumberInput
          value={price ?? ''}
          onChange={handlePriceChange}
          min={0}
          decimalScale={2}
          fixedDecimalScale
          placeholder="Prix par défaut"
          disabled={curatedCatalog && !enabled}
          hideControls
          suffix="€"
        />
      </Table.Td>
    </Table.Tr>
  )
})
